import React, { FC } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
  Paper,
  styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { FhirResourceTypes } from '@oh-vcp/components-common';

interface EconsultClinicalReportSelectorProps {
  selectedItems: string[]; // array of option codes, e.g. 'allergyIntolerance'
  onSelectionChanged: (selectedItems: string[]) => void;
  onSelectionDeleted?: (selectedItem: string) => void;
}

type Option = { code: string; value: string };

const options: Option[] = [
  { code: FhirResourceTypes.AllergyIntolerance, value: 'Allergy Intolerance' },
  { code: FhirResourceTypes.Condition, value: 'Condition' },
  { code: FhirResourceTypes.FamilyMemberHistory, value: 'Family Member History' },
  { code: FhirResourceTypes.MedicationRequest, value: 'Medications' },
  { code: FhirResourceTypes.Observation, value: 'Observations' },
  { code: FhirResourceTypes.Immunization, value: 'Immunization' },
  { code: FhirResourceTypes.Procedure, value: 'Procedure' },
  { code: FhirResourceTypes.DiagnosticReport, value: 'Diagnostic Report' },
  { code: FhirResourceTypes.Encounter, value: 'Encounter' }
].sort((a, b) => a.value.localeCompare(b.value));

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 920 // adjust as needed
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 44,
  borderRadius: 10,
  backgroundColor: '#f7f9ff',
  border: '1.5px solid #d6e0fb',
  fontSize: 14,
  fontWeight: 500,
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '.MuiChip-deleteIcon': {
    color: '#222',
    fontSize: 20
  }
}));

const OptionCheckbox = styled(Checkbox)(({ theme }) => ({
  fontSize: 14,
  padding: 12,
  marginRight: 12,
  '& .MuiSvgIcon-root': {
    fontSize: 14,
    borderRadius: 6
  },
  '&.Mui-checked': {
    color: theme.palette.primary.main
  }
}));

const StyledPaper = styled(Paper)(() => ({
  // give list more vertical space so items look like the screenshot
  '.MuiAutocomplete-listbox': {
    paddingTop: 6,
    paddingBottom: 6
  }
}));

const EconsultClinicalReportSelector: FC<EconsultClinicalReportSelectorProps> = ({
  selectedItems,
  onSelectionChanged,
  onSelectionDeleted
}) => {
  const { t } = useTranslation();

  // map selected codes -> option objects for Autocomplete's value
  const valueObjects: Option[] = options.filter((o) => selectedItems.includes(o.code));

  // Note: newValue is Option[] because the Autocomplete is multiple
  const handleChange = (_event: React.SyntheticEvent, newValue: Option[]) => {
    onSelectionChanged(newValue.map((v) => v.code));
  };

  return (
    <Container>
      {/* Autocomplete generics: <Option, true, false, false> */}
      <Autocomplete<Option, true, false, false>
        multiple
        options={options}
        getOptionLabel={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        value={valueObjects}
        disableCloseOnSelect
        onChange={handleChange}
        PaperComponent={(props) => <StyledPaper {...props} />}
        popupIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { onDelete, ...chipProps } = getTagProps({ index });
            return (
              <StyledChip
                key={option.code}
                label={option.value}
                {...chipProps}
                onDelete={(e) => {
                  e.stopPropagation();
                  onSelectionDeleted?.(option.code);
                }}
                deleteIcon={<CloseIcon />}
              />
            );
          })
        }
        renderOption={(props, option, { selected }) => (
          <li {...props} style={{ paddingLeft: 16, paddingTop: 12, paddingBottom: 12 }}>
            <OptionCheckbox
              checked={selected}
              icon={<span />} // don't show default radio icon
              checkedIcon={<CheckIcon />}
            />
            <Typography sx={{ fontSize: 14 }}>{option.value}</Typography>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Select category..."
            InputProps={{
              ...params.InputProps,
              sx: {
                fontSize: 14,
                minHeight: 56,
                padding: '8px 10px',
                '& .MuiInputBase-input': { padding: 0 }
              }
            }}
          />
        )}
        sx={{
          '.MuiAutocomplete-inputRoot': {
            padding: '6px'
          }
        }}
      />
    </Container>
  );
};

export default EconsultClinicalReportSelector;
